import { useCallback, useContext, useEffect, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { useConfirm } from '@/components/ui/confirm-dialog';

/*
  useUnsavedChanges(isDirty, options) — protect unsaved edits.

    const { confirmLeave, markClean } = useUnsavedChanges(dirtyCount > 0);
    // page-internal switches (tabs, "close drawer"):
    const switchTab = async (next) => { if (await confirmLeave()) setTab(next); };
    // save, then leave — call markClean() right before navigate():
    const saveAndClose = async () => { await save(); markClean(); setDirty(false); navigate('/projects'); };

  While `isDirty` is true:
  - closing / reloading the browser tab shows the browser's "Leave site?" prompt (beforeunload);
  - in-app navigation through react-router (<Link>, navigate()) asks first with the app's
    ConfirmDialog ("Leave without saving?") and only navigates on "Discard changes".
  The app uses <BrowserRouter> (not a data router), so useBlocker() is unavailable; the hook guards
  navigator.push/replace instead. Browser Back/Forward is not intercepted (beforeunload covers reloads).

  markClean(): the edits are saved — let the next navigation (and confirmLeave) through without asking.
  Needed because `setDirty(false); navigate(...)` in one handler navigates before React re-renders,
  i.e. while the guard is still armed. The pass is used up by that navigation and cancelled as soon as
  the page renders dirty again, so call it immediately before navigate(), not before an await.

  Options: title, description, confirmLabel, cancelLabel.

  withoutLeaveGuard(fn): runs fn with the navigation guard switched off, for URL writes that only
  record in-page view state (filters, sort, tab, view — hooks/useUrlState does this). Such a write
  does not leave the page, so it must not ask "Leave without saving?". Pages that unmount dirty
  content on a tab switch still ask first with confirmLeave() (useTabParam's `guard` option).
*/

const DEFAULTS = {
  title: 'Leave without saving?',
  description: 'You have unsaved changes on this page. If you leave now they will be lost.',
  confirmLabel: 'Discard changes',
  cancelLabel: 'Keep editing',
};

// One patch per navigator; every mounted guard registers a blocker in the set.
// A blocker is { takePass(): boolean, ask(): Promise<boolean> }.
const guards = new WeakMap();

// > 0 while withoutLeaveGuard() runs: navigations in that window are in-page view-state updates.
let bypassDepth = 0;

/** Run `fn` (which navigates synchronously, e.g. setSearchParams) without the leave guard. */
export function withoutLeaveGuard(fn) {
  bypassDepth += 1;
  try {
    return fn();
  } finally {
    bypassDepth -= 1;
  }
}

function installGuard(navigator) {
  let entry = guards.get(navigator);
  if (entry) return entry;
  const original = { push: navigator.push, replace: navigator.replace };
  entry = { original, blockers: new Set(), prompting: false };
  const wrap = (method) => function guardedNavigation(...args) {
    if (bypassDepth > 0) return original[method].apply(navigator, args);
    // Every guard is asked; a guard whose page called markClean() spends its one pass here.
    const blocking = [...entry.blockers].filter((blocker) => !blocker.takePass());
    if (!blocking.length) return original[method].apply(navigator, args);
    if (entry.prompting) return undefined;
    entry.prompting = true;
    blocking[blocking.length - 1].ask()
      .then((ok) => {
        entry.prompting = false;
        if (ok) original[method].apply(navigator, args);
      })
      .catch(() => { entry.prompting = false; });
    return undefined;
  };
  navigator.push = wrap('push');
  navigator.replace = wrap('replace');
  guards.set(navigator, entry);
  return entry;
}

function releaseGuard(navigator, blocker) {
  const entry = guards.get(navigator);
  if (!entry) return;
  entry.blockers.delete(blocker);
  if (entry.blockers.size === 0) {
    navigator.push = entry.original.push;
    navigator.replace = entry.original.replace;
    guards.delete(navigator);
  }
}

export function useUnsavedChanges(isDirty, options = {}) {
  const confirm = useConfirm();
  const navigation = useContext(UNSAFE_NavigationContext);
  const navigator = navigation?.navigator;
  const dirtyRef = useRef(Boolean(isDirty));
  const passRef = useRef(false);
  const optionsRef = useRef(options);
  dirtyRef.current = Boolean(isDirty) && !passRef.current;
  optionsRef.current = options;

  const askToLeave = useCallback(() => {
    const opts = { ...DEFAULTS, ...optionsRef.current };
    return confirm({
      title: opts.title,
      description: opts.description,
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      tone: 'danger',
    });
  }, [confirm]);

  /** Resolves true when it is OK to continue (nothing dirty, or the user chose to discard). */
  const confirmLeave = useCallback(async () => {
    if (!dirtyRef.current) return true;
    return askToLeave();
  }, [askToLeave]);

  /** The edits were saved: the next navigation / confirmLeave() goes through without a prompt. */
  const markClean = useCallback(() => {
    passRef.current = true;
    dirtyRef.current = false;
  }, []);

  // A pass is only good until the page commits another dirty render (new edits after the save).
  useEffect(() => {
    if (isDirty && passRef.current) {
      passRef.current = false;
      dirtyRef.current = true;
    }
  });

  useEffect(() => {
    if (!isDirty) return undefined;
    const handleBeforeUnload = (event) => {
      if (passRef.current) return undefined;
      event.preventDefault();
      // Chrome/Edge still need returnValue set to show the prompt.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty || !navigator || typeof navigator.push !== 'function') return undefined;
    const blocker = {
      takePass: () => {
        if (!passRef.current) return false;
        passRef.current = false;
        return true;
      },
      ask: () => askToLeave(),
    };
    installGuard(navigator).blockers.add(blocker);
    return () => releaseGuard(navigator, blocker);
  }, [isDirty, navigator, askToLeave]);

  return { confirmLeave, markClean, isDirty: Boolean(isDirty) };
}

export default useUnsavedChanges;
